import api from "../utils/api";
import { isGuid } from "../utils/string";
import { pack } from "./alert";

/**
 * Normalize dates so API receives serializable values.
 * Keeps null/undefined as-is.
 */
const toIsoOrNull = (value) => {
    if (!value) return null;
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value.toISOString();
    }
    return value;
};

const normalizeAddress = (address = {}) => ({
    ...(address || {}),
});

const normalizePerson = (person = {}) => ({
    ...(person || {}),
    generation: person?.generation ?? "",
    firstName: person?.firstName ?? "",
    middleName: person?.middleName ?? "",
    lastName: person?.lastName ?? "",
    email: person?.email ?? "",
    phone: person?.phone ?? "",
    ssn: String(person?.ssn ?? "").replace(/\D/g, "").slice(0, 9),
    birthDate: toIsoOrNull(person?.birthDate),
    gender: person?.gender ?? "unknown",
    Residence: normalizeAddress(person?.Residence || {}),
    Birthplace: normalizeAddress(person?.Birthplace || {}),
});

const normalizeParty = (party = {}) => ({
    ...(party || {}),
    uuid: party?.uuid ?? null,
    maiden: party?.maiden ?? "",
    restore: !!party?.restore,
    pregnant: !!party?.pregnant,
    dueDate: toIsoOrNull(party?.dueDate),
    marriages: Number(party?.marriages ?? 1),
    education: Number(party?.education ?? 0),
    driversLicenseNumber: party?.driversLicenseNumber ?? "",
    driversLicenseState: party?.driversLicenseState ?? "",
    military: party?.military ?? "unknown",
    title: party?.title ?? "spouse",
    employerName: party?.employerName ?? "",
    employerPhone: party?.employerPhone ?? "",
    employerUuid: party?.employerUuid ?? null,
    Person: normalizePerson(party?.Person || {}),
    EmployerAddress: normalizeAddress(party?.EmployerAddress || {}),
});

const normalizeChild = (child = {}) => ({
    ...(child || {}),
    uuid: child?.uuid ?? null,
    legalCustody: child?.legalCustody ?? "joint",
    physicalCustody: child?.physicalCustody ?? "joint",
    Person: normalizePerson(child?.Person || {}),
});

const normalizeChildren = (children = []) =>
    (Array.isArray(children) ? children : []).map(normalizeChild);

const normalizeMarriage = (payload = {}) => ({
    LastCohabitation: payload?.LastCohabitation ?? {},
    Location: payload?.Location ?? {},
    marriageDate: toIsoOrNull(payload?.marriageDate),
    separationDate: toIsoOrNull(payload?.separationDate),
    livingTogether: !!payload?.livingTogether,
    intentDate: toIsoOrNull(payload?.intentDate),
    intentCode: payload?.intentCode ?? "plaintiff",
    agreementDate: toIsoOrNull(payload?.agreementDate),
});

export const getDivorce = ({ uuid }) => async () => {
    try {
        if (!isGuid(uuid)) return { success: false, item: null };

        const res = await api.get(`/cases/profiles/divorces/i/${uuid}`);
        return res.data;
    } catch (err) {
        console.log(err);
        return { success: false, error: err };
    }
};

export const saveDivorce = (payload = {}) => async (dispatch) => {
    try {
        const {
            uuid,
            Plaintiff,
            Defendant,
            Children,
            ...rest
        } = payload;

        if (!isGuid(uuid)) {
            return { success: false, message: "Invalid matter UUID." };
        }

        const marriage = normalizeMarriage(rest);

        const body = {
            uuid,
            ...rest,
            ...marriage,
            Plaintiff: normalizeParty(Plaintiff || {}),
            Defendant: normalizeParty(Defendant || {}),
            Children: normalizeChildren(Children || []),
        };

        const res = await api.post(`/cases/profiles/divorces`, body);
        dispatch(pack(["Divorce details saved"], "success"));
        return res.data;
    } catch (err) {
        dispatch(
            pack(
                [err?.response?.data?.error?.message || err?.message || "Save failed"],
                "danger"
            )
        );
        return { success: false, error: err };
    }
};

export const patchDivorce = ({ uuid, patch = {} }) => async (dispatch) => {
    try {
        if (!isGuid(uuid)) {
            return { success: false, message: "Invalid matter UUID." };
        }

        const normalizedPatch = {
            ...patch,
            marriageDate: toIsoOrNull(patch?.marriageDate),
            separationDate: toIsoOrNull(patch?.separationDate),
            intentDate: toIsoOrNull(patch?.intentDate),
            agreementDate: toIsoOrNull(patch?.agreementDate),
        };

        if (patch?.Plaintiff) {
            normalizedPatch.Plaintiff = normalizeParty(patch.Plaintiff);
        }

        if (patch?.Defendant) {
            normalizedPatch.Defendant = normalizeParty(patch.Defendant);
        }

        if (patch?.Children) {
            normalizedPatch.Children = normalizeChildren(patch.Children);
        }

        const res = await api.patch(`/cases/profiles/divorces/${uuid}`, normalizedPatch);
        dispatch(pack(["Divorce details updated"], "success"));
        return res.data;
    } catch (err) {
        dispatch(
            pack(
                [err?.response?.data?.error?.message || err?.message || "Update failed"],
                "danger"
            )
        );
        return { success: false, error: err };
    }
};

export const searchDivorce = ({
    page = 0,
    size = 10,
    order = "updatedAt",
    direction = "desc",
    search = null,
}) => async () => {
    try {
        const safeSearch = search ?? "null";
        const res = await api.get(
            `/cases/profiles/divorces/p/${page}/s/${size}/o/${order}/${direction}/${safeSearch}`
        );
        return res.data;
    } catch (err) {
        console.log(err);
        return { success: false, error: err };
    }
};